import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  Button,
  useMediaQuery,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { SCOPE_CR_STATUS, SCOPE_CR_STATUS_DATA } from "../../../../config/constants";
import { toast } from "react-toastify";
import { Setting } from "../../../../utils/Setting";
import { getApiData } from "../../../../utils/APIHelper";
import { useTheme } from "@emotion/react";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  return text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  textTransform: "capitalize",
}));

// Derive a friendly title from a scope_change_request row.
const crTitle = (cr) => {
  if (!cr) return "—";
  const isDesign = cr.change_type === "DESIGN_UPDATE"
    || (Array.isArray(cr.design_items) && cr.design_items.length > 0);
  if (isDesign) {
    const n = Array.isArray(cr.design_items) ? cr.design_items.length : 0;
    return `Design Change (${n} drawing${n === 1 ? "" : "s"})`;
  }
  return cr?.oldScope?.name || cr?.draftScope?.name || "Scope Change";
};

// Map status → dot color for the status cell.
const statusColor = (status) =>
  SCOPE_CR_STATUS_DATA[status]?.color || "#FFAD5C";

const MobileView = ({ list, handleChange, handleApprove, handleReject, loadingItems }) => (
  <div style={mobileStyles.container}>
    <div style={mobileStyles.changelogContainer}>
      {list?.map((item) => {
        const canAct = item?.status === SCOPE_CR_STATUS.SUBMITTED;
        const busy = loadingItems.includes(Number(item?.id));
        return (
          <div key={item.id} style={mobileStyles.changelogCard}>
            <div style={mobileStyles.row}>
              <Typography
                style={mobileStyles.nameValue}
                onClick={() => handleChange(item)}
              >
                {crTitle(item)}
              </Typography>
            </div>

            <div style={mobileStyles.row}>
              <div style={mobileStyles.dateStatusContainer}>
                <div style={mobileStyles.dateItem}>
                  <Typography style={mobileStyles.dateLabel}>Created</Typography>
                  <Typography style={mobileStyles.dateValue}>
                    {item?.created_at
                      ? moment(item.created_at).format("DD MMM, YYYY")
                      : "-"}
                  </Typography>
                </div>
                <div style={mobileStyles.statusItem}>
                  <Typography style={mobileStyles.statusLabel}>Status</Typography>
                  <div style={mobileStyles.statusContainer}>
                    <div
                      style={{
                        ...mobileStyles.statusDot,
                        backgroundColor: statusColor(item?.status),
                      }}
                    />
                    <Typography style={mobileStyles.statusText}>
                      {SCOPE_CR_STATUS_DATA[item?.status]?.label || getStatusDisplayText(item?.status)}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            <div style={mobileStyles.row}>
              <Chip
                size="small"
                label={getStatusDisplayText(item?.change_type || "SCOPE")}
                sx={{ alignSelf: "flex-start", background: "#0D0D0E", color: "#fff" }}
              />
            </div>

            {canAct && (
              <div style={mobileStyles.buttonContainer}>
                <Button
                  onClick={() => handleApprove(item)}
                  variant="contained"
                  disabled={busy}
                  sx={{
                    backgroundColor: "#4D6BF6",
                    borderRadius: "12px",
                    textTransform: "none",
                    width: "100%",
                  }}
                >
                  Approve
                </Button>
                <Button
                  onClick={() => handleReject(item)}
                  variant="outlined"
                  disabled={busy}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    width: "100%",
                    mt: 1,
                  }}
                >
                  Reject
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const mobileStyles = {
  container: { padding: "2vh 0", backgroundColor: "#fff" },
  changelogContainer: { display: "flex", flexDirection: "column", gap: "18px" },
  changelogCard: {
    backgroundColor: "#fff",
    padding: "16px 0",
    borderBottom: "1px solid #D7D8D9",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  row: { display: "flex", flexDirection: "column", gap: "8px" },
  nameValue: { fontSize: "14px", fontWeight: "500", color: "#333", cursor: "pointer" },
  dateStatusContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
  },
  dateItem: { display: "flex", flexDirection: "column", alignItems: "flex-start" },
  dateLabel: { fontSize: "10px", color: "#666", marginBottom: "2px" },
  dateValue: { fontSize: "12px", color: "#333", fontWeight: "500" },
  statusItem: { display: "flex", flexDirection: "column", alignItems: "flex-start" },
  statusLabel: { fontSize: "10px", color: "#666", marginBottom: "2px" },
  statusContainer: { display: "flex", alignItems: "center", gap: "4px" },
  statusDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    border: "3px solid rgb(229, 229, 229)",
  },
  statusText: { fontSize: "12px", color: "#333", fontWeight: "500" },
  buttonContainer: { marginTop: "8px" },
};

export default function ChangeRequestList({ list, refetch }) {
  const [loadingItems, setLoadingItems] = useState([]);
  const [rejectCr, setRejectCr] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleChange = (item) => {
    navigate(`/project/details/${projectId}/change-request?change=${item.id}`);
  };

  const withBusy = (crId, fn) => async () => {
    setLoadingItems((prev) => [...prev, Number(crId)]);
    try {
      await fn();
      refetch?.();
    } catch (err) {
      toast.error(err?.message || "Something went wrong!");
    } finally {
      setLoadingItems((prev) => prev.filter((it) => it !== Number(crId)));
    }
  };

  const handleApprove = (cr) =>
    withBusy(cr.id, async () => {
      await getApiData(
        Setting.endpoints.changeRequest.approve(cr.id),
        "POST",
        { comment: "Approved by contractor" },
        { "Content-Type": "application/json" },
        false
      );
      toast.success("Change request approved.");
    })();

  const handleReject = (cr) => {
    setRejectReason("");
    setRejectCr(cr);
  };

  const confirmReject = async () => {
    const reason = rejectReason.trim();
    if (!reason || !rejectCr) return;
    const cr = rejectCr;
    setRejectCr(null);
    setRejectReason("");
    await withBusy(cr.id, async () => {
      await getApiData(
        Setting.endpoints.changeRequest.reject(cr.id),
        "POST",
        { comment: reason, rejection_reason: reason },
        { "Content-Type": "application/json" },
        false
      );
      toast.success("Change request rejected.");
    })();
  };

  const rejectBusy = rejectCr ? loadingItems.includes(Number(rejectCr.id)) : false;

  const rejectDialog = (
    <Dialog
      open={!!rejectCr}
      onClose={() => !rejectBusy && setRejectCr(null)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 600 }}>Reject change request</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "#667085", mb: 1.5 }}>
          Let the admin know why you're rejecting this change request.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          placeholder="Reason for rejection..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          disabled={rejectBusy}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setRejectCr(null)}
          disabled={rejectBusy}
          sx={{ textTransform: "none", color: "#667085" }}
        >
          Cancel
        </Button>
        <Button
          onClick={confirmReject}
          variant="contained"
          color="error"
          disabled={rejectBusy || !rejectReason.trim()}
          sx={{ textTransform: "none" }}
        >
          {rejectBusy ? "Rejecting..." : "Confirm reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (isMobile) {
    return (
      <>
        <MobileView
          list={list}
          handleChange={handleChange}
          handleApprove={handleApprove}
          handleReject={handleReject}
          loadingItems={loadingItems}
        />
        {rejectDialog}
      </>
    );
  }

  return (
    <>
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        boxShadow: "none",
        maxHeight: "75vh",
        overflowY: "scroll",
        scrollbarWidth: "none",
      }}
    >
      <Table
        sx={{
          "& thead": { "& th": { color: "#949DAA", fontWeight: "500" } },
          "& tbody": {
            "& tr:hover": { backgroundColor: "#f7f7f7" },
            "& tr": { borderBottom: "none" },
          },
        }}
      >
        <TableHead>
          <TableRow>
            <StyledTableCell>Name</StyledTableCell>
            <StyledTableCell>Type</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell>Created</StyledTableCell>
            <StyledTableCell align="right">Action</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list?.map((item) => {
            const canAct = item?.status === SCOPE_CR_STATUS.SUBMITTED;
            const busy = loadingItems.includes(Number(item?.id));
            return (
              <TableRow key={item.id}>
                <StyledTableCell>
                  <p
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "260px",
                      cursor: "pointer",
                      fontSize: "14px",
                      color: "#0D0D0E",
                    }}
                    onClick={() => handleChange(item)}
                  >
                    {crTitle(item)}
                  </p>
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    size="small"
                    label={getStatusDisplayText(item?.change_type || "SCOPE")}
                    sx={{ background: item?.change_type === "DESIGN_UPDATE" ? "#7c3aed" : "#0D0D0E", color: "#fff" }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Box
                      style={{
                        width: "15px",
                        height: "15px",
                        borderRadius: "50%",
                        backgroundColor: statusColor(item?.status),
                        border: "3px solid #e5e5e5",
                      }}
                    />
                    <Typography
                      style={{ fontSize: "14px", marginLeft: "8px", color: "#0D0D0E" }}
                    >
                      {SCOPE_CR_STATUS_DATA[item?.status]?.label ||
                        getStatusDisplayText(item?.status)}
                    </Typography>
                  </div>
                </StyledTableCell>
                <StyledTableCell>
                  <p style={{ color: "#949DAA", fontWeight: "500" }}>
                    {item?.created_at
                      ? moment(item.created_at).format("MMM DD, YYYY")
                      : "-"}
                  </p>
                </StyledTableCell>
                <StyledTableCell align="right">
                  {canAct ? (
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <Button
                        onClick={() => handleApprove(item)}
                        variant="contained"
                        color="primary"
                        disabled={busy}
                        sx={{ textTransform: "none" }}
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(item)}
                        variant="outlined"
                        disabled={busy}
                        sx={{ textTransform: "none" }}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => handleChange(item)}
                      variant="text"
                      sx={{ textTransform: "none" }}
                    >
                      View
                    </Button>
                  )}
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
    {rejectDialog}
    </>
  );
}
